import React, { useState, useEffect } from 'react';
import { useImageContext } from '../contexts/ImageContext';

interface ImageWithFallbackProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | string[]; 
  fallbackSrc?: string;
  storageKey?: string;
  width?: string | number;
  height?: string | number;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ 
  src: initialSrc, 
  fallbackSrc = 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=60&w=800&auto=format&fit=crop', 
  alt, 
  className,
  storageKey,
  width,
  height,
  ...props 
}) => {
  let contextValues;
  try {
    contextValues = useImageContext();
  } catch (e) {
    contextValues = null;
  }

  const getEffectiveSrc = () => {
    if (storageKey && contextValues?.images[storageKey]) {
      return contextValues.images[storageKey];
    }
    return initialSrc;
  };

  const effectiveSrc = getEffectiveSrc();

  const generateCandidates = (input: string | string[]) => {
    const inputs = Array.isArray(input) ? input : [input];
    const candidates = new Set<string>();

    inputs.forEach(path => {
      if (!path) return;
      if (path.startsWith('data:')) {
        candidates.add(path);
        return;
      }
      candidates.add(path);
      if (path.startsWith('/')) {
         candidates.add(path.slice(1));
         candidates.add('.' + path);
      } else {
         candidates.add('/' + path);
         candidates.add('./' + path);
      }
    });
    return Array.from(candidates);
  };
  
  const sourceCandidates = generateCandidates(effectiveSrc);
  
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const [imgSrc, setImgSrc] = useState<string>(sourceCandidates[0]);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    const candidates = generateCandidates(effectiveSrc);
    setCurrentSrcIndex(0);
    setImgSrc(candidates[0]);
    setIsFallback(false);
  }, [effectiveSrc]);

  const handleError = () => {
    const nextIndex = currentSrcIndex + 1;
    if (nextIndex < sourceCandidates.length) {
      setCurrentSrcIndex(nextIndex);
      setImgSrc(sourceCandidates[nextIndex]);
    } else if (!isFallback) {
      setIsFallback(true);
      setImgSrc(fallbackSrc);
    }
  };

  const finalClassName = isFallback 
    ? `${className} opacity-80` 
    : className;

  return (
    <img
      {...props}
      src={imgSrc}
      alt={alt}
      width={width}
      height={height}
      className={finalClassName}
      onError={handleError}
    />
  );
};

export default ImageWithFallback;